/**
 * StorylineDescriptionCard — the "Description" card on the storyline detail page
 * (storyline-design.md §3.3, Figma 3246:43741). Title matches the Asset Detail
 * "Details" h2 (16px/700) with the "Updated:" stamp inline on the same row + a divider
 * beneath. Read view renders the description as MARKDOWN (same MarkdownRenderer as the
 * Asset detail 8-Element card) or an italic placeholder; ✎ Edit switches to a
 * Textarea (save → UpdateStorylineDescription, a dedicated single-field op — never
 * bundled with name/assetIds). Empty string clears the description (backend stores it
 * NOT NULL DEFAULT '').
 */
import { useState } from 'react';
import { Alert, Box, Button, Container, SpaceBetween, Textarea } from '@amzn/awsui-components-react';
import MarkdownRenderer from '../../../../components/ChatPanel/MarkdownRenderer';
import { storylineClient } from '../../../client/storylineClient';
import './StorylineDescriptionCard.css';
import { denialHint } from '../../../utils/authorization';

/** Format an ISO instant as "YYYY/M/D HH:mm:ss" (matches the Figma "Updated:" stamp). */
function formatUpdated(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

export default function StorylineDescriptionCard({
  storylineId,
  description,
  updatedAt,
  onSaved,
}: {
  storylineId: string;
  description: string;
  updatedAt?: string;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startEdit = () => {
    setDraft(description);
    setError(null);
    setEditing(true);
  };

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      await storylineClient.updateDescription(storylineId, draft);
      setEditing(false);
      onSaved(); // reload the storyline so the card + timestamp refresh
    } catch (e) {
      setError(denialHint(e) ?? '保存描述失败，请重试。');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Container
      header={
        <div className="ivt-storyline-desc-head">
          {/* "Description" title (matches the Asset Detail "Details" h2) + the
              "Updated:" stamp INLINE on the same row; actions pushed right. The
              Container header slot draws the divider under this row. */}
          <h2 className="ivt-storyline-desc-title">Description</h2>
          {updatedAt && (
            <span className="ivt-storyline-desc-updated">Updated: {formatUpdated(updatedAt)}</span>
          )}
          <div className="ivt-storyline-desc-actions">
            {editing ? (
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" disabled={saving} onClick={() => setEditing(false)}>
                  取消
                </Button>
                <Button variant="primary" loading={saving} onClick={() => void handleSave()}>
                  保存
                </Button>
              </SpaceBetween>
            ) : (
              <Button variant="inline-link" iconName="edit" onClick={startEdit}>
                Edit Description
              </Button>
            )}
          </div>
        </div>
      }
    >
      {error && (
        <Box margin={{ bottom: 's' }}>
          <Alert type="error">{error}</Alert>
        </Box>
      )}
      {editing ? (
        <Textarea
          value={draft}
          onChange={({ detail }) => setDraft(detail.value)}
          rows={6}
          disabled={saving}
          placeholder="Add description (optional)"
          ariaLabel="Storyline 描述"
        />
      ) : (
        <div className="ivt-storyline-desc-readbody">
          {description.trim() ? (
            // Description is authored in markdown — render it the same way the Asset
            // detail 8-Element card does (shared dependency-free MarkdownRenderer).
            <MarkdownRenderer markdown={description} />
          ) : (
            <Box color="text-status-inactive">
              <span style={{ fontStyle: 'italic' }}>Add description (optional)</span>
            </Box>
          )}
        </div>
      )}
    </Container>
  );
}
